import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, CheckCircle2, Mail, MapPin, Users2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      toast.success("Message sent — we'll be in touch.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-bg">
      <header className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="contact-brand-link">
          <div className="h-10 w-10 rounded-xl bg-[#0B1E36] grid place-items-center">
            <Sparkles className="h-5 w-5 text-[#C9A961]" />
          </div>
          <div>
            <div className="font-serif text-xl leading-tight text-[#0B1E36]">Future Counts OS™</div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase">by the Future Counts Collective</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="contact-back" className="text-sm font-medium text-slate-600 hover:text-[#0B1E36] inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back home</Link>
          <Link to="/about" data-testid="contact-to-about" className="text-sm font-medium text-slate-600 hover:text-[#0B1E36] px-4 py-2">About</Link>
        </div>
      </header>

      {submitted ? (
        <section className="max-w-2xl mx-auto px-6 lg:px-10 py-24 text-center fade-up" data-testid="contact-success">
          <div className="h-16 w-16 mx-auto rounded-full bg-[#EC4899] grid place-items-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-white" />
          </div>
          <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">Message received</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#0B1E36] leading-tight mb-4">Thank you.</h1>
          <p className="text-slate-600 leading-relaxed max-w-lg mx-auto">
            We've received your message and will respond personally within the next few business days. A confirmation has been sent to your email.
          </p>
          <div className="mt-14 flex justify-center gap-4">
            <Link to="/" data-testid="success-home" className="bg-[#0B1E36] text-white px-6 py-3 rounded-full font-semibold btn-lift hover:bg-[#132a4a]">Back home</Link>
            <Link to="/partners" data-testid="success-partners" className="bg-white border border-slate-200 text-[#0B1E36] px-6 py-3 rounded-full font-semibold btn-lift hover:bg-slate-50">Become a partner</Link>
          </div>
        </section>
      ) : (
        <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">Contact</div>
            <h1 className="font-serif text-4xl lg:text-5xl text-[#0B1E36] leading-[1.1] mb-4">Let's talk.</h1>
            <p className="text-slate-600 leading-relaxed mb-10">
              Have a question about the EQ Initiative™, an idea for partnership, or a story about your organization? We read every message and respond personally.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-[#0B1E36] grid place-items-center shrink-0">
                  <Mail className="h-4 w-4 text-[#C9A961]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Email</div>
                  <a href="mailto:karen@thefuturecounts.org" className="text-[#0B1E36] font-serif text-lg hover:text-[#EC4899]" data-testid="contact-email-link">karen@thefuturecounts.org</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-[#0B1E36] grid place-items-center shrink-0">
                  <Users2 className="h-4 w-4 text-[#C9A961]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Become a Partner</div>
                  <Link to="/partners" className="text-[#0B1E36] font-serif text-lg hover:text-[#EC4899]">Apply to join the movement →</Link>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-[#0B1E36] grid place-items-center shrink-0">
                  <MapPin className="h-4 w-4 text-[#C9A961]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Founded by</div>
                  <div className="text-[#0B1E36] font-serif text-lg">Karen Pinckney</div>
                  <div className="text-xs text-slate-500 mt-0.5">Founder & Executive Director</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white p-8 lg:p-10 rounded-3xl border border-slate-200 shadow-[0_20px_50px_rgba(11,30,54,0.06)]">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Your name</label>
                  <input required value={form.name} onChange={upd("name")} data-testid="contact-name"
                    className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Your email</label>
                  <input required type="email" value={form.email} onChange={upd("email")} data-testid="contact-email"
                    className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Subject</label>
                  <input required value={form.subject} onChange={upd("subject")} data-testid="contact-subject"
                    placeholder="What are you reaching out about?"
                    className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Your message</label>
                  <textarea required rows={6} value={form.message} onChange={upd("message")} data-testid="contact-message"
                    className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none resize-none" />
                </div>
              </div>
              <button
                type="submit" disabled={loading} data-testid="contact-submit"
                className="mt-6 w-full bg-[#EC4899] text-white py-3.5 rounded-full font-semibold text-base btn-lift hover:bg-[#DB2777] disabled:opacity-60 shadow-[0_8px_24px_rgba(236,72,153,0.25)]">
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          </div>
        </section>
      )}

      <footer className="border-t border-slate-200 py-8 bg-white mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-xs text-slate-500">
          <div>© 2026 Future Counts Collective</div>
          <div>Building a better future — together.</div>
        </div>
      </footer>
    </div>
  );
}
